import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Iceutility } from '../iceutility';
import { BreadcrumType } from '../../services/baseapp.service';
import { Router } from '@angular/router';

const HistoryLimit = 30;
const HistoryStorageKey = "breadcrumbHistory";

export interface Breadcrumb {
  display: string;
  data: any;
  active: boolean;
  link: string;
  history: any[];
}

export interface SessionBreadcrumbHistory {
  [providerName: string]: {
    yards: History[],
    branches: History[]
  }
}

export interface BreadcrumbsState {
  FARM: Breadcrumb;
  YARD: Breadcrumb;
  TREE: Breadcrumb;
  BRANCH: Breadcrumb;
  LEAVES: any[];
  TOOLBARS?: any[];
  HIDE?: boolean;
}

export interface History {
  data: any,
  link: string,
  display: string
}

const BreadcrumbKeyMap: { [key: string]: string } = {
  [BreadcrumType.btFarm.toString()]: 'FARM',
  [BreadcrumType.btYard.toString()]: 'YARD',
  [BreadcrumType.btBranch.toString()]: 'BRANCH',
  [BreadcrumType.btLeaves.toString()]: 'LEAVES',
  [BreadcrumType.btTree.toString()]: 'TREE'
};

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private breadcrumbsSubject = new BehaviorSubject<BreadcrumbsState>(this.getInitialBeadcrumbsState());
  private breadcrumbHistory: SessionBreadcrumbHistory = {};

  constructor(private router: Router) { }

  getInitialBeadcrumbsState(): BreadcrumbsState {
    return {
      FARM: { display: '', data: null, active: false, link: '', history: [] },
      YARD: { display: '', data: null, active: false, link: '', history: [] },
      TREE: { display: '', data: null, active: false, link: '', history: [] },
      BRANCH: { display: '', data: null, active: false, link: '', history: [] },
      LEAVES: []
    };
  }

  getBreadcrumbs$(): Observable<BreadcrumbsState> {
    return this.breadcrumbsSubject.asObservable();
  }

  getBreadcrumbHistory(): SessionBreadcrumbHistory {
    let data = sessionStorage.getItem(HistoryStorageKey);
    return Iceutility.isdefined(data) ? JSON.parse(data) : {};
  }

  getBreadcrumbHistoryByProvider(providerName: string): { yards: History[], branches: History[] } {
    var sessionHistory = this.getBreadcrumbHistory();
    if (Iceutility.isdefined(sessionHistory) && sessionHistory[providerName] != null) {
      return {
        yards: sessionHistory[providerName].yards,
        branches: sessionHistory[providerName].branches
      };
    } else {
      return { yards: [], branches: [] };
    }
  }

  setBreadcrumbHistory(providerName: string, history: SessionBreadcrumbHistory): void {
    var sessionHistory = this.getBreadcrumbHistory();

    if (!Iceutility.isdefined(sessionHistory)) {
      sessionHistory = {};
    }

    if (sessionHistory[providerName] != null) {
      var yards: History[] = sessionHistory[providerName].yards;
      var branches: History[] = sessionHistory[providerName].branches;

      const keyToCompare = 'display';
      for (let i = 0; i < history[providerName].yards.length; i++) {
        let historyObj = history[providerName].yards[i];

        if (!Iceutility.isStringUndefinedNullOrEmpty(historyObj.display)) {
          const index = yards.findIndex(y => y[keyToCompare] === historyObj[keyToCompare]);
          if (index === -1) {
            yards.push(historyObj);

            if (yards.length > HistoryLimit) {
              yards.shift();
            }
          } else {
            if (yards[index].link !== historyObj.link) {
              yards[index].link = historyObj.link; // Update the existing entry if the link is different
            }
          }
        }
      }

      for (let i = 0; i < history[providerName].branches.length; i++) {
        let historyObj = history[providerName].branches[i];

        const index = branches.findIndex(b => b[keyToCompare] === historyObj[keyToCompare]);
        if (index === -1) {
          branches.push(historyObj);

          if (branches.length > HistoryLimit) {
            branches.shift();
          }
        } else {
          if (branches[index].link !== historyObj.link) {
            branches[index].link = historyObj.link; // Update the existing entry if the link is different
          }
        }
      }

      sessionHistory[providerName].yards = yards;
      sessionHistory[providerName].branches = branches;
      this.setSessionHistory(sessionHistory);
    } else {
      sessionHistory[providerName] = { yards: [], branches: [] };
      sessionHistory[providerName].yards = history[providerName].yards;
      sessionHistory[providerName].branches = history[providerName].branches;
      this.setSessionHistory(sessionHistory);
    }
  }

  getBreadcrumbs(leafText?: string): BreadcrumbsState {
    let current = this.breadcrumbsSubject.getValue(),
      history = this.getBreadcrumbHistoryByProvider(sessionStorage.getItem("g_currentact"));

    if (Iceutility.isdefined(history)) {
      current.YARD.history = history.yards.filter(y => y.display != current.YARD.display);
      current.BRANCH.history = history.branches.filter(b => b.display != current.BRANCH.display);
    }
    return current;
  }

  setBreadcrumbs(state: BreadcrumbsState): void {
    this.breadcrumbsSubject.next({ ...state });
  }

  updateBreadcrumbs(breadcrumbs: Partial<BreadcrumbsState>, ...keysToReset: string []): void {
    keysToReset.forEach(key => {
      this.resetBreadcrumb(this.getKeyName(key));
    });

    const current = this.getBreadcrumbs();
    
    if (Iceutility.isdefined(breadcrumbs.FARM)) {
      breadcrumbs.FARM.data = !Iceutility.isdefined(breadcrumbs.FARM.data) ? "" : breadcrumbs.FARM.data;
      breadcrumbs.FARM.link = Iceutility.isStringUndefinedNullOrEmpty(breadcrumbs.FARM.link) ? this.router.url : breadcrumbs.FARM.link;
    }
    if (Iceutility.isdefined(breadcrumbs.YARD)) {
      breadcrumbs.YARD.data = !Iceutility.isdefined(breadcrumbs.YARD.data) ? "" : breadcrumbs.YARD.data;
      breadcrumbs.YARD.link = Iceutility.isStringUndefinedNullOrEmpty(breadcrumbs.YARD.link) ? this.router.url : breadcrumbs.YARD.link;
    }
    if (Iceutility.isdefined(breadcrumbs.TREE)) {
      breadcrumbs.TREE.data = !Iceutility.isdefined(breadcrumbs.TREE.data) ? "" : breadcrumbs.TREE.data;
      breadcrumbs.TREE.link = Iceutility.isStringUndefinedNullOrEmpty(breadcrumbs.TREE.link) ? this.router.url : breadcrumbs.TREE.link;
    }
    if (Iceutility.isdefined(breadcrumbs.BRANCH)) {
      breadcrumbs.BRANCH.data = !Iceutility.isdefined(breadcrumbs.BRANCH.data) ? "" : breadcrumbs.BRANCH.data;
      breadcrumbs.BRANCH.link = Iceutility.isStringUndefinedNullOrEmpty(breadcrumbs.BRANCH.link) ? this.router.url : breadcrumbs.BRANCH.link;
    }

    // Store the history for YARD and BRANCH only
    if (sessionStorage.getItem("g_currentact") !== null) {
      const act = sessionStorage.getItem("g_currentact");

      const yardHistory: History = Iceutility.isdefined(breadcrumbs.YARD) ? {
        display: breadcrumbs.YARD.display,
        data: breadcrumbs.YARD.data,
        link: breadcrumbs.YARD.link
      } : {
        display: "",
        data: "",
        link: ""
      };

      const branchHistory: History = Iceutility.isdefined(breadcrumbs.BRANCH) ? {
        display: breadcrumbs.BRANCH.display,
        data: breadcrumbs.BRANCH.data,
        link: breadcrumbs.BRANCH.link
      } : {
        display: "",
        data: "",
        link: ""
      };

      this.setBreadcrumbHistory(act, {
        [act]: {
          yards: [yardHistory],
          branches: [branchHistory],
        }
      });
    }

    // Yard display is empty but history exists, take the first history item to show as current
    if (current.YARD.history.length > 0) {
      let yardHistory = current.YARD.history[0];
      if (Iceutility.isdefined(yardHistory) && yardHistory.display !== '') {
        current.YARD.display = yardHistory.display;
        current.YARD.data = yardHistory.data;
        current.YARD.link = yardHistory.link;
        current.YARD.active = true;
      }
    }

    this.breadcrumbsSubject.next({
      ...current,
      ...breadcrumbs
    });
  }

  updateBreadcrumb(key: string, breadcrumb: Partial<Breadcrumb>, forceReset: boolean = false): void {
    if (forceReset) this.reset();

    const current = this.getBreadcrumbs();
    key = this.getKeyName(key);

    if (key === "YARD" && Iceutility.isdefined(breadcrumb.data) && Iceutility.isdefined(breadcrumb.data.account)) {
      sessionStorage.setItem("patientAccount", breadcrumb.data.account); /// TODO: use existing function
    }

    // setting the data so that breadcrumb would be visible even if only display is passed
    if (!Iceutility.isdefined(breadcrumb.data)) {
      breadcrumb.data = {};
    }

    // setting the data to inlcude the current patient
    if (key === "BRANCH") {
      let currentPatient = sessionStorage.getItem("patientAccount"); /// TODO: use existing function

      if (!Iceutility.isStringUndefinedNullOrEmpty(currentPatient)) {
        breadcrumb.data.account = currentPatient;
      }
    }

    // set the current url if link is not provided
    if (Iceutility.isStringUndefinedNullOrEmpty(breadcrumb.link)) {
      breadcrumb.link = this.router.url;
    }

    // Store the history for YARD and BRANCH only
    if (sessionStorage.getItem("g_currentact") !== null && (key === "YARD" || key === "BRANCH")) {
      const act = sessionStorage.getItem("g_currentact");

      const history: History = {
        display: breadcrumb.display,
        data: breadcrumb.data,
        link: breadcrumb.link
      };

      this.setBreadcrumbHistory(act, {
        [act]: {
          yards: key === "YARD" ? [history] : [],
          branches: key === "BRANCH" ? [history] : [],
        }
      });
    }

    // Yard display is empty but history exists, take the first history item to show as current
    // only do this when route is valid within the context of patient
    if (current.YARD.history.length > 0 && location.pathname.indexOf("/accounting") >= 0 && location.pathname.indexOf("/accounting/summary") === -1) {
      let yardHistory = current.YARD.history[0];
      if (Iceutility.isdefined(yardHistory) && yardHistory.display !== '') {
        current.YARD.display = yardHistory.display;
        current.YARD.data = yardHistory.data;
        current.YARD.link = yardHistory.link;
        current.YARD.active = true;
      }
    }

    if (['FARM', 'YARD', 'TREE', 'BRANCH'].includes(key) && typeof current[key] === 'object' && current[key] !== null) {
      this.breadcrumbsSubject.next({
        ...current,
        [key]: { ...(current[key] as object), ...breadcrumb }
      });
    }
  }

  updateBreadcrumbData(data: any, type: BreadcrumType): void {
    let current = this.getBreadcrumbs();

    if (type === BreadcrumType.btBranch) {
      current.BRANCH.data = data;
    }
    if (type === BreadcrumType.btYard) {
      current.YARD.data = data;
    }
    this.breadcrumbsSubject.next({ ...current });

  }

  addLeaf(leaf: Breadcrumb): void {
    let data = this.breadcrumbsSubject.getValue();
    if (Iceutility.isdefined(data)) {
      // Only add if not already present by display
      const exists = data.LEAVES.findIndex(l => l.display === leaf.display) !== -1;
      if (!exists) {
        const newLeaves = [...data.LEAVES, leaf];
        this.breadcrumbsSubject.next({ ...data, LEAVES: newLeaves });
      }
    }
  }

  getYardData() {
    let state = this.getBreadcrumbs(),
      yardData = {};

    if (Iceutility.isdefined(state) && Iceutility.isdefined(state.YARD) && Iceutility.isdefined(state.YARD.data)) {
      yardData = state.YARD.data;

      try {
        // if the context doesn't have any data, fallback to history and fetch the data
        if (!Iceutility.isdefined(yardData) || Object.keys(yardData).length === 0) {
          let history = this.getBreadcrumbHistory(),
          currentAccount = sessionStorage.getItem("g_currentact");

          if (Iceutility.isdefined(history) && history[currentAccount] && history[currentAccount].yards && history[currentAccount].yards.length > 0) {
            let yardHistory = history[currentAccount].yards;
            let foundHistory = yardHistory.find(yh => yh.display === state.YARD.display);

            if (Iceutility.isdefined(foundHistory) && Iceutility.isdefined(foundHistory.data) && Object.keys(foundHistory.data).length > 0) {
              yardData = foundHistory.data;
            }
          }
        }
      } catch (ex) {
        console.log(ex);
      }
    }

    return yardData;
  }

  updateLeaves(leaves: any[]): void {
    const current = this.getBreadcrumbs();
    this.breadcrumbsSubject.next({ ...current, LEAVES: leaves });
  }

  updateHistory(key: keyof BreadcrumbsState, history: any[]): void {
    const current = this.getBreadcrumbs();
    if (['FARM', 'YARD', 'TREE', 'BRANCH'].includes(key as string) && typeof current[key] === 'object' && current[key] !== null) {
      this.breadcrumbsSubject.next({
        ...current,
        [key]: { ...(current[key] as object), history }
      });
    }
  }

  resetBreadcrumb(key: string) {
    if (Iceutility.isStringUndefinedNullOrEmpty(key)) return;

    var current = this.getBreadcrumbs();
    current[this.getKeyName(key)] = {
      display: "",
      data: {},
      active: false
    };
    this.breadcrumbsSubject.next({ ...current });
  }

  resetBreadcrumbs(keys: string[]) {
    if (Iceutility.isStringUndefinedNullOrEmpty(keys)) return;

    var current = this.getBreadcrumbs();
    const initialBreadcrumbsState = this.getInitialBeadcrumbsState();

    for (let key of keys) {
      const keyName = this.getKeyName(key);
      current[keyName] = initialBreadcrumbsState[keyName];
    }
    this.breadcrumbsSubject.next({ ...current });
  }

  setSessionHistory(history: SessionBreadcrumbHistory) {
    sessionStorage.setItem(HistoryStorageKey, JSON.stringify(history));
  }

  clearSessionHistory() {
    sessionStorage.removeItem(HistoryStorageKey);
    this.breadcrumbsSubject.next(this.getInitialBeadcrumbsState());
  }

  reset(): void {
    this.breadcrumbsSubject.next(this.getInitialBeadcrumbsState());
  }

  private getKeyName(key: string): string {
    return BreadcrumbKeyMap[key] || key.toUpperCase();
  }
}
